// Modal.js
import React, { useEffect, useState, useCallback  } from "react";
import "./Modal.css";
import { useFormContext } from "react-hook-form";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import { getUser } from "redux/features/userSliceWithTokenValidation";
import { useCreateDossierMutation } from "actions/dossiers/mutations";
import { toastApiErrors } from "utils/toastApiError";
import GeneriqueModal from "./GeneriqueModal";
import ButtonCancel from "./buttons/ButtonCancel";
import ButtonGreen from "./buttons/ButtonGreen";
import GeographySelector from "./GeographySelector/GeographySelector";

const FORMES_JURIDIQUES = [
  "Société en nom collectif (SNC)",
  "Société en commandite simple (SCS)",
  "Société à responsabilité limitée (SARL)",
  "Société Unipersonnelle à Responsabilité Limitée (SUARL)",
  "Société anonyme (SA)",
  "Société par actions simplifiée (SAS)",
  "SAS Unipersonnelle (SASU)",
  "Société en commandite par actions (SCA)",
  "Société civile professionnelle (SCP)",
  "Société civile immobilière (SCI)",
  "Société civile (autre)",
  "Groupement d'intérêt économique (GIE)",
  "Entreprise Individuelle",
  "AUTRE",
];

// Modal.js - diff des changements principaux

// 1. Ajouter l'étape
const STEPS = ["Identification", "Coordonnées", "Capital utilisé","Télécommunications"];

// 2. Comptes de charges SYSCOHADA les plus courants
const COMPTES_CHARGES = [
  { code: "601", label: "601 – Achats de marchandises" },
  { code: "604", label: "604 – Achats de matières premières & fournitures" },
  { code: "611", label: "611 – Transport sur achats" },
  { code: "621", label: "621 – Loyers et charges locatives" },
  { code: "622", label: "622 – Locations et charges de copropriété" },
  { code: "623", label: "623 – Redevances de crédit-bail" },
  { code: "624", label: "624 – Entretien et réparations" },
  { code: "625", label: "625 – Primes d'assurance" },
  { code: "626", label: "626 – Études et recherches" },
  { code: "631", label: "631 – Frais bancaires" },
  { code: "641", label: "641 – Impôts et taxes" },
  { code: "661", label: "661 – Rémunérations du personnel" },
  { code: "658", label: "658 – Charges diverses" },
];

// 3. Composant Étape 4 - à ajouter dans le return du Modal
// Remplace le bloc {currentStep === 3 && ...} footer et ajoute après lui :

// ── Step indicator ─────────────────────────────────────────────────────────────
const StepIndicator = ({ currentStep }) => (
  <div style={{ display: "flex", alignItems: "center", marginBottom: "24px", padding: "0 10px" }}>
    {STEPS.map((label, index) => {
      const stepNum = index + 1;
      const isCompleted = stepNum < currentStep;
      const isActive = stepNum === currentStep;
      return (
        <React.Fragment key={label}>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", minWidth: "90px" }}>
            <div style={{
              width: "32px", height: "32px", borderRadius: "50%",
              display: "flex", alignItems: "center", justifyContent: "center",
              fontWeight: 700, fontSize: "14px", transition: "all 0.3s",
              backgroundColor: isCompleted || isActive ? "#009688" : "#e0e0e0",
              color: isCompleted || isActive ? "#fff" : "#888",
              border: isActive ? "3px solid #00796b" : "3px solid transparent",
            }}>
              {isCompleted ? "✓" : stepNum}
            </div>
            <span style={{
              marginTop: "4px", fontSize: "11px",
              fontWeight: isActive ? 700 : 400,
              color: isActive ? "#009688" : isCompleted ? "#009688" : "#aaa",
              whiteSpace: "nowrap",
            }}>
              {label}
            </span>
          </div>
          {index < STEPS.length - 1 && (
            <div style={{
              flex: 1, height: "3px", marginBottom: "18px",
              backgroundColor: isCompleted ? "#009688" : "#e0e0e0",
              transition: "background-color 0.3s",
            }} />
          )}
        </React.Fragment>
      );
    })}
  </div>
);

const SectionDivider = ({ label }) => (
  <div style={{ display: "flex", flexDirection: "row", justifyContent: "center", alignItems: "center" }}>
    <div style={{ width: "auto" }}>{label}</div>
    <div style={{ width: "100%", height: "1px", backgroundColor: "rgba(0,0,0,0.6)" }} />
  </div>
);

const FieldRow = ({ label, children, error }) => (
  <div className="form-group" style={{ display: "flex", flexDirection: "row", alignItems: "center", padding: "0px 10px" }}>
    <label className="form-label" style={{ width: "35%" }}>{label}</label>
    {children}
    {error && <div className="text-danger">{error.message}</div>}
  </div>
);

// ══════════════════════════════════════════════════════════════════════════════
const Modal = ({ isOpen, onClose, onDossierCreated }) => {
  const userStatus = useSelector(getUser);
  const [createDossier, { isLoading }] = useCreateDossierMutation();
  const [currentStep, setCurrentStep] = useState(1);

  // ✅ useFormContext DOIT être appelé EN PREMIER avant tout watch()
  const { register, handleSubmit, formState: { errors }, reset, watch, setValue, trigger } = useFormContext();
  const formeJuridiqueValue = watch("forme_juridique");

  // ✅ Maintenant watch est disponible, capitalSocial peut être défini
  const capitalSocial = watch("capital_social") || 0;
  const contrepartie462Selected = watch("contrepartie_462");
  const targetAccountCode = contrepartie462Selected ? "462" : "521";
  const targetAccountLabel = contrepartie462Selected ? "Associés" : "Banque";

  const [modeCapital, setModeCapital] = useState("lignes");
  const [lignesDepenses, setLignesDepenses] = useState([
    { compte: "658", libelle: "", montant: "" }
  ]);
  const [soldeRestant, setSoldeRestant] = useState("");


  const totalDepenses = lignesDepenses.reduce(
    (sum, l) => sum + (parseFloat(l.montant) || 0), 0
  );
  const resteAVentiler = parseFloat(capitalSocial) - totalDepenses;

  const addLigne = () =>
    setLignesDepenses((prev) => [...prev, { compte: "658", libelle: "", montant: "" }]);

  const removeLigne = (i) =>
    setLignesDepenses((prev) => prev.filter((_, idx) => idx !== i));

  const updateLigne = (i, field, value) =>
    setLignesDepenses((prev) =>
      prev.map((l, idx) => (idx === i ? { ...l, [field]: value } : l))
    );

  
  const thStyle = { padding: "6px 8px", textAlign: "left", fontWeight: 600, color: "#555", borderBottom: "1px solid #ddd" };
  const tdStyle = { padding: "4px 6px" };

  // Champs à valider par étape
  const stepFields = {
    1: ["nom_entreprise", "forme_juridique", "capital_social",
        ...(formeJuridiqueValue === "AUTRE" ? ["forme_juridique_personnalisee"] : [])],
    2: ["adresse"],
    3: ["telephone", "email"],
    4: [], // ✅ aucun champ obligatoire à l'étape 4
  };

  const handleNext = async () => {
    const valid = await trigger(stepFields[currentStep]);
    if (valid) setCurrentStep((s) => s + 1);
  };

  const handleBack = () => setCurrentStep((s) => s - 1);

  const handleKeyPress = useCallback((event) => {
  if (event.key === "Enter") {
    event.preventDefault();
    if (currentStep < STEPS.length) handleNext();
    else handleSubmit(onSubmit)();
  }
}, [currentStep]); // ✅ se recrée uniquement quand currentStep change

  useEffect(() => {
  document.addEventListener("keydown", handleKeyPress);
  return () => document.removeEventListener("keydown", handleKeyPress);
}, [handleKeyPress]); // ✅ cleanup propre à chaque changement

  // ── Logique de création inchangée ──────────────────────────────────────────
  const onSubmit = (data) => {
  const { forme_juridique_personnalisee, ...rest } = data;
  const normalizedForme =
    rest.forme_juridique === "AUTRE" ? forme_juridique_personnalisee : rest.forme_juridique;

  // ── Construire les écritures de dépenses ──────────────────────────────────
  let depenses_capital = [];

  if (modeCapital === "lignes") {
    depenses_capital = lignesDepenses
      .filter((l) => parseFloat(l.montant) > 0)
      .map((l) => ({
        compte_debit: l.compte,
        libelle: l.libelle || "Dépense capital",
        montant: parseFloat(l.montant),
      }));
  } else if (modeCapital === "solde_restant") {
    const montantDepense = parseFloat(capitalSocial) - parseFloat(soldeRestant || 0);
    if (montantDepense > 0) {
      depenses_capital = [{
        compte_debit: "658",
        libelle: "Charges diverses sur capital",
        montant: montantDepense,
      }];
    }
  } else if (modeCapital === "tout_depense") {
    depenses_capital = [{
      compte_debit: "658",
      libelle: "Charges diverses sur capital",
      montant: parseFloat(capitalSocial),
    }];
  }

  const dossierData = {
    ...rest,
    forme_juridique: normalizedForme,
    comptable: userStatus?.id,
    depenses_capital, // ← nouveau champ envoyé au backend
  };

  createDossier(dossierData)
    .unwrap()
    .then(() => {
      toast.success("Création du dossier réussi !");
      reset();
      setCurrentStep(1);
      setLignesDepenses([{ compte: "658", libelle: "", montant: "" }]);
      setSoldeRestant("");
      setModeCapital("lignes");
      if (onDossierCreated) {
        onDossierCreated();
      }
      onClose();
    })
    .catch((error) => {
      console.error(error);
      toastApiErrors(error);
    });
};

  const gridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(3, 1fr)",
  marginLeft: "5%",
};

  return (
    <GeneriqueModal className="modal-overlay" onClose={onClose} title={"Ajouter un dossier"} isOpen={isOpen}>
      <StepIndicator currentStep={currentStep} />

      <form className="auth-form" autoComplete="off" onSubmit={handleSubmit(onSubmit)}>

        {/* STEP 1 ─ Identification */}
        {currentStep === 1 && (
          <>
            <div style={{ display: "flex", flexDirection: "row", justifyContent: "center", alignItems: "center" }}>
              <div className="Id" style={{ width: "auto" }}>Identification</div>
              <div style={{ width: "100%", height: "1px", backgroundColor: "rgba(0,0,0,0.6)" }} />
            </div>
            <div style={gridStyle}>
              <FieldRow label="Raison sociale* :" error={errors.nom_entreprise}>
                <input className="form-control custom-rounded" placeholder="Nom de l'entreprise" style={{ width: "100%" }}
                  {...register("nom_entreprise", { required: "Nom de l'entreprise obligatoire" })} />
              </FieldRow>

              <FieldRow label="Forme Juridique* :" error={errors.forme_juridique}>
                <select className="form-control custom-rounded" style={{ width: "100%" }}
                  {...register("forme_juridique", { required: "Forme juridique obligatoire" })}>
                  <option value="">Sélectionnez une forme</option>
                  {FORMES_JURIDIQUES.map((forme) => (
                    <option key={forme} value={forme}>{forme === "AUTRE" ? "Autre (préciser)" : forme}</option>
                  ))}
                </select>
              </FieldRow>

              {formeJuridiqueValue === "AUTRE" && (
                <FieldRow label="Précisez la forme" error={errors.forme_juridique_personnalisee}>
                  <input className="form-control custom-rounded" placeholder="Ex : Société Coopérative Agricole" style={{ width: "100%" }}
                    {...register("forme_juridique_personnalisee", {
                      validate: (value) => formeJuridiqueValue === "AUTRE"
                        ? (value && value.trim() !== "") || "Merci de préciser la forme juridique" : true,
                    })} />
                </FieldRow>
              )}

              <FieldRow label="Secteur d'activité :" error={errors.secteur_activite}>
                <input className="form-control custom-rounded" placeholder="Secteur" style={{ width: "100%" }} {...register("secteur_activite")} />
              </FieldRow>

              <FieldRow label="Capital social* :" error={errors.capital_social}>
                <input className="form-control custom-rounded" placeholder="Capital Social" type="number" min="0" step="any" style={{ width: "100%" }}
                  {...register("capital_social", { required: "Capital obligatoire", valueAsNumber: true, min: { value: 0, message: "Le capital social ne peut pas être négatif" } })} />
              </FieldRow>

              <div className="form-group" style={{ display: "flex", flexDirection: "row", alignItems: "center", padding: "0px 10px" }}>
  <label className="form-label" style={{ width: "auto", marginRight: "2px" }}>Description :</label>
                <input className="form-control custom-rounded" placeholder="Description" style={{ width: "100%" }} {...register("description")} />
              </div>
            </div>
          </>
        )}

        {/* STEP 2 ─ Coordonnées */}
        {currentStep === 2 && (
          <>
            <SectionDivider label="Coordonnées" />
            <div style={gridStyle}>
              <FieldRow label="Adresse* :" error={errors.adresse}>
                <input className="form-control custom-rounded" placeholder="Adresse" style={{ width: "100%" }}
                  {...register("adresse", { required: "Adresse obligatoire" })} />
              </FieldRow>
              <FieldRow label="Code Postal :" error={errors.code_postal}>
                <input className="form-control custom-rounded" placeholder="Code Postal" style={{ width: "100%" }} {...register("code_postal")} />
              </FieldRow>
              <GeographySelector register={register} errors={errors} setValue={setValue} />
            </div>
          </>
        )}

        {/* STEP 3 ─ Télécommunications + Immatriculation */}
        {currentStep === 4 && (
          <>
            <SectionDivider label="Télécommunications" />
            <div style={gridStyle}>
              <FieldRow label="Numéro de Téléphone* :" error={errors.telephone}>
                <input className="form-control custom-rounded" placeholder="Téléphone" type="tel" style={{ width: "100%" }}
                  {...register("telephone", {
                    required: "N° Tel doit être que des entiers et moins de 09 chiffres",
                    maxLength: { value: 9, message: "Le numéro de téléphone ne doit pas dépasser 9 chiffres" },
                  })} />
              </FieldRow>
              <FieldRow label="Adresse Email* :" error={errors.email}>
                <input className="form-control custom-rounded" placeholder="Email" type="email" style={{ width: "100%" }}
                  {...register("email", { required: "Email Obligatoire | Respecter le format mail" })} />
              </FieldRow>
              <FieldRow label="Site Internet :" error={errors.site_internet}>
                <input className="form-control custom-rounded" placeholder="Site internet" style={{ width: "100%" }} {...register("site_internet")} />
              </FieldRow>
            </div>

            <SectionDivider label="Immatriculation" />
            <div style={gridStyle}>
              <FieldRow label="Siret :" error={errors.imma_siret}>
                <input className="form-control custom-rounded" placeholder="Siret" style={{ width: "100%" }} {...register("imma_siret")} />
              </FieldRow>
              <FieldRow label="Code NAF :" error={errors.imma_code_naf}>
                <input className="form-control custom-rounded" placeholder="Code NAF" style={{ width: "100%" }} {...register("imma_code_naf")} />
              </FieldRow>
              <FieldRow label="Identifiant TVA :" error={errors.imma_idt_tva}>
                <input className="form-control custom-rounded" placeholder="Identifiant TVA" style={{ width: "100%" }} {...register("imma_idt_tva")} />
              </FieldRow>
            </div>
          </>
        )}

        {currentStep === 3 && (
          <>
            <SectionDivider label="Utilisation du capital social" />
            <div style={{ padding: "0 10px", marginBottom: 12 }}>
              <p style={{ color: "#555", fontSize: 13, marginBottom: 12 }}>
                Capital social déclaré :{" "}
                <strong style={{ color: "#009688" }}>
                  {Number(capitalSocial).toLocaleString("fr-SN")} FCFA
                </strong>
              </p>

              <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "18px" }}>
                <input
                  type="checkbox"
                  id="contrepartie_462"
                  style={{ width: "16px", height: "16px", cursor: "pointer", accentColor: "#009688" }}
                  {...register("contrepartie_462")}
                />
                <label
                  htmlFor="contrepartie_462"
                  style={{ fontSize: "13px", fontWeight: "600", color: "red", cursor: "pointer", userSelect: "none" }}
                >
                  {"Enregistrer la contrepartie du capital dans le compte d'associés (462) au lieu de la caisse"}
                </label>
              </div>

              {/* Choix du mode */}
              <div style={{ display: "flex", gap: 12, marginBottom: 18, flexWrap: "wrap" }}>
                {[
                  { key: "lignes", label: "Je détaille mes dépenses" },
                  { key: "solde_restant", label: "Je connais le solde restant" },
                  { key: "tout_depense", label: "Tout a été dépensé" },
                ].map(({ key, label }) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setModeCapital(key)}
                    style={{
                      padding: "8px 14px",
                      borderRadius: 20,
                      border: "2px solid",
                      borderColor: modeCapital === key ? "#009688" : "#ccc",
                      backgroundColor: modeCapital === key ? "#e0f2f1" : "#fff",
                      color: modeCapital === key ? "#009688" : "#666",
                      fontWeight: modeCapital === key ? 700 : 400,
                      cursor: "pointer",
                      fontSize: 13,
                    }}
                  >
                    {label}
                  </button>
                ))}
              </div>

              {/* MODE 1 : Saisie ligne par ligne */}
              {modeCapital === "lignes" && (
                <div>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                    <thead>
                      <tr style={{ backgroundColor: "#f5f5f5" }}>
                        <th style={thStyle}>Compte de charge</th>
                        <th style={thStyle}>Libellé</th>
                        <th style={thStyle}>Montant (FCFA)</th>
                        <th style={thStyle}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {lignesDepenses.map((ligne, i) => (
                        <tr key={i}>
                          <td style={tdStyle}>
                            <select
                              className="form-control custom-rounded"
                              value={ligne.compte}
                              onChange={(e) => updateLigne(i, "compte", e.target.value)}
                            >
                              {COMPTES_CHARGES.map((c) => (
                                <option key={c.code} value={c.code}>{c.label}</option>
                              ))}
                            </select>
                          </td>
                          <td style={tdStyle}>
                            <input
                              className="form-control custom-rounded"
                              placeholder="Ex: Loyer bureau"
                              value={ligne.libelle}
                              onChange={(e) => updateLigne(i, "libelle", e.target.value)}
                            />
                          </td>
                          <td style={tdStyle}>
                            <input
                              className="form-control custom-rounded"
                              type="number"
                              min="0"
                              placeholder="0"
                              value={ligne.montant}
                              onChange={(e) => updateLigne(i, "montant", e.target.value)}
                            />
                          </td>
                          <td style={tdStyle}>
                            {lignesDepenses.length > 1 && (
                              <button
                                type="button"
                                onClick={() => removeLigne(i)}
                                style={{ background: "none", border: "none", color: "#e53935", cursor: "pointer", fontSize: 18 }}
                              >
                                ✕
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  <button
                    type="button"
                    onClick={addLigne}
                    style={{ marginTop: 8, color: "#009688", background: "none", border: "1px dashed #009688", borderRadius: 6, padding: "4px 12px", cursor: "pointer", fontSize: 13 }}
                  >
                    + Ajouter une ligne
                  </button>

                  {/* Résumé */}
                  <div style={{ marginTop: 14, padding: 10, backgroundColor: resteAVentiler < 0 ? "#ffebee" : "#e8f5e9", borderRadius: 8, fontSize: 13 }}>
                    <div>Total dépenses saisi : <strong>{totalDepenses.toLocaleString("fr-SN")} FCFA</strong></div>
                    <div style={{ color: resteAVentiler < 0 ? "#c62828" : "#2e7d32" }}>
                      Solde restant en {targetAccountLabel.toLowerCase()} ({targetAccountCode}) : <strong>{resteAVentiler.toLocaleString("fr-SN")} FCFA</strong>
                      {resteAVentiler < 0 && " ⚠️ Le total dépasse le capital social !"}
                    </div>
                    {resteAVentiler > 0 && (
                      <div style={{ color: "#888", fontSize: 12, marginTop: 4 }}>
                        Le solde de {resteAVentiler.toLocaleString("fr-SN")} FCFA restera en {targetAccountCode} ({targetAccountLabel}).
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* MODE 2 : Solde restant connu */}
              {modeCapital === "solde_restant" && (
                <div>
                  <FieldRow label="Montant restant en banque (FCFA) :">
                    <input
                      className="form-control custom-rounded"
                      type="number"
                      min="0"
                      max={capitalSocial}
                      placeholder="Ex: 500000"
                      value={soldeRestant}
                      onChange={(e) => setSoldeRestant(e.target.value)}
                      style={{ width: "100%" }}
                    />
                  </FieldRow>
                  {soldeRestant !== "" && (
                    <div style={{ marginTop: 10, padding: 10, backgroundColor: "#e8f5e9", borderRadius: 8, fontSize: 13 }}>
                      <div>Restera en <strong>{targetAccountCode} – {targetAccountLabel}</strong> : {Number(soldeRestant).toLocaleString("fr-SN")} FCFA</div>
                      <div>
                        Passé en <strong>658 – Charges diverses</strong> :{" "}
                        {(parseFloat(capitalSocial) - parseFloat(soldeRestant || 0)).toLocaleString("fr-SN")} FCFA
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* MODE 3 : Tout dépensé */}
              {modeCapital === "tout_depense" && (
                <div style={{ padding: 14, backgroundColor: "#fff3e0", borderRadius: 8, fontSize: 13 }}>
                  <p>
                    ✅ L&apos;intégralité du capital ({Number(capitalSocial).toLocaleString("fr-SN")} FCFA) sera passée en{" "}
                    <strong>658 – Charges diverses</strong>.
                  </p>
                  <p style={{ color: "#888" }}>
                    Écriture : Débit 658 / Crédit {targetAccountCode} = {Number(capitalSocial).toLocaleString("fr-SN")} FCFA
                  </p>
                </div>
              )}
            </div>
          </>
        )}

        {/* Footer navigation */}
        <div className="modal-footer" style={{ display: "flex", justifyContent: "space-between", marginTop: "16px" }}>
          <div>
            {currentStep > 1
              ? <ButtonCancel text="← Retour" onClick={handleBack} />
              : <ButtonCancel text="Annuler" onClick={onClose} />}
          </div>
          <div>
            {currentStep < STEPS.length
              ? <ButtonGreen text="Suivant →" type="button" onClick={handleNext} />
              : <ButtonGreen text={isLoading ? "Chargement..." : "Terminer"} type="submit" disabled={isLoading} />}
          </div>
        </div>
      </form>
    </GeneriqueModal>
  );
};

export default Modal;